/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   types for web-zumper mainMap redux state
 */

import type { MetroRentReport, RentReport, Unit } from '@zcode/bizos'
import type { Origin } from '@zcode/nr-utils'
import type { Mutable } from '@zcode/utility-types'
import type { Empty, OptionalIfUndefined } from '@zcode/utility-types'
import type { DistributedOmit } from 'type-fest'
import type { Building, Property } from '../Detail'
import type { Filters } from '../Filters'
import type {
  FloorplanListing,
  Listable,
  Listing,
  NormalFloorplanListingData,
  NormalListingData,
} from '../PermanentAddress'
import type { SavedSearch } from '../SavedSearchesView'
import type { DlcMessagedProperty } from '../bizos'

export enum MODAL_TYPE {
  FLAG = '@@nzr/modals/FLAG',
  HASHTAG = '@@nzr/modals/HASHTAG',
  MESSAGE = '@@nzr/modals/MESSAGE',
  MORE_AMENITIES = '@@nzr/modals/MORE_AMENITIES',
  MULTI_MESSAGE = '@@nzr/modals/MULTI_MESSAGE',
  INACTIVE_SAVE_SEARCH = '@@nzr/modals/INACTIVE_SAVE_SEARCH',
  PRICE_DATA_TABLE = '@@nzr/modals/PRICE_DATA_TABLE',
  RATINGS_AND_REVIEWS = '@@nzr/modals/RATINGS_AND_REVIEWS',
  RATINGS_AND_REVIEWS_MOBILE = '@@nzr/modals/RATINGS_AND_REVIEWS_MOBILE',
  RESIDENT_QUOTES = '@@nzr/modals/RESIDENT_QUOTES',
  SAVE_SEARCH = '@@nzr/modals/SAVE_SEARCH',
  SAVED_SEARCH_OPTION = '@@nzr/modals/SAVED_SEARCH_OPTION',
  SHORT_TERM_AVAILABILITY = '@@nzr/modals/SHORT_TERM_AVAILABILITY',
  SHORT_TERM_AVAILABILITY_V1 = '@@nzr/modals/SHORT_TERM_AVAILABILITY_V1',
}

export interface Modals {
  readonly open: MODAL_TYPE | null
  readonly flag: FlagModal
  readonly hashtag: HashtagModal
  readonly inactiveSaveSearch: InactiveSaveSearchModal
  readonly message: MessageModal
  readonly metroRentReport: MetroRentReport | null
  readonly multiMessage: MultiMessageModal
  readonly saveSearch: SaveSearchModal
  readonly savedSearchOption: SavedSearchOptionModal
}

// Should replace with bizos when done
export interface AutocompleteBoxType {
  minLng?: number
  minLat?: number
  maxLng?: number
  maxLat?: number
}

export interface FlagModal {
  readonly analyticChannel: '' | 'Detail' | 'Permanent Address'
  readonly entity: Building | Listing<NormalListingData> | null | undefined
}

export type RentReportCityState =
  | 'atlanta-ga'
  | 'austin-tx'
  | 'boston-ma'
  | 'charlotte-nc'
  | 'chicago-il'
  | 'dallas-tx'
  | 'detroit-mi'
  | 'denver-co'
  | 'houston-tx'
  | 'kansas-city-mo'
  | 'las-vegas-nv'
  | 'los-angeles-ca'
  | 'miami-fl'
  | 'minneapolis-mn'
  | 'nashville-tn'
  | 'new-york-ny'
  | 'norfolk-va'
  | 'orlando-fl'
  | 'philadelphia-pa'
  | 'phoenix-az'
  | 'portland-or'
  | 'sacramento-ca'
  | 'salt-lake-city-ut'
  | 'san-diego-ca'
  | 'san-francisco-ca'
  | 'seattle-wa'
  | 'tampa-fl'
  | 'washington-dc'

export interface HashtagModal {
  readonly cityState: RentReportCityState | null
  readonly modalType: 'rent-report' | 'city-guide' | null
  readonly rentReport: RentReport | null
}

export interface InactiveSaveSearchModal {
  readonly origin: Origin | undefined
  readonly selectedFloorplan?: FloorplanListing<NormalFloorplanListingData> | null
}

export interface InitialMessageData {
  readonly completionEvtName?:
    | 'MessageSimilar'
    | 'MessageSimilarMobile'
    | 'Schedule A Tour Modal'
    | 'Message Modal'
    | 'MessageInline'
    | 'Schedule A Tour MessageInline'
    | 'One Tap'
    | 'Schedule A Tour One Tap'
  readonly email?: string | null
  readonly feedNames?: ReadonlyArray<string | null | undefined>
  readonly ignoreEqualCTAOrigin?: boolean
  readonly listingIds?: readonly number[]
  readonly messagedBuildingIds?: readonly number[]
  readonly messagedListingIds?: readonly number[]
  readonly messageModified?: boolean
  readonly moveIn?: string | null
  readonly msg?: string
  readonly name?: string
  readonly origin: Origin
  readonly profile?: never
  readonly phone?: string | null
  /**
   * When the renter would like to tour, in "EEEE, LLL d 'at' h:mm aaa" format
   */
  readonly selectedTimes?: readonly string[]
  /**
   * When the renter would like to tour, in "yyyy-MM-dd'T'kk:mm':00.000" format
   */
  readonly selectedTimesISO8601?: readonly string[]
  readonly viewingId?: number | undefined
}

export interface MessageDataForBuilding extends InitialMessageData {
  readonly building: (Listable | Property) & {
    readonly isBuilding: true
  }
  readonly buildingIds: readonly number[] | undefined
  readonly listing?: never
  readonly listingIds: readonly number[] | undefined
  readonly messagedBuildingIds: readonly number[]
  readonly originalTarget?: never
}

export interface MessageDataForListing extends InitialMessageData {
  readonly building?: never
  readonly buildingIds?: never
  readonly listing: (Listable | Property) & {
    readonly isBuilding?: false | undefined
  }
  readonly listingIds: readonly number[]
  readonly messagedListingIds: readonly number[]
  readonly originalTarget?: never
}

export interface MessageDataForMulti extends InitialMessageData {
  readonly building?: never
  readonly buildingIds?: never
  readonly listing?: never
  readonly listingIds: readonly number[]
  readonly messagedFpToBldgMap: { readonly [floorplanId: number]: number }
  readonly messagedBuildingIds: readonly number[]
  readonly messagedListingIds: readonly number[]
  readonly originalTarget: Listable | Property
}

export type MessageData = MessageDataForBuilding | MessageDataForListing | MessageDataForMulti

export interface MessageModalDataEmpty {
  readonly analyticChannel: '' | undefined
  readonly ctaName: string | undefined
  readonly currentTab?: string
  readonly entity: Empty<Listable | Property> | null | undefined
  readonly equalCTAIntent: '' | undefined
  readonly hasInstantTourAvailability: undefined
  readonly isFilterAd: boolean | null | undefined
  readonly isQuestionModal: boolean | undefined
  readonly onlyShowFloorplanGroup: boolean | undefined
  readonly origin: undefined
  readonly selectedFloorplan: FloorplanListing<NormalFloorplanListingData> | undefined
  readonly selectedUnit: Unit | null | undefined
  readonly showAgentInfo: boolean | undefined
  readonly showing: false
  readonly similarListings: readonly never[]
  readonly viewingId: number | undefined
  readonly viewingTime: string | undefined
}

export interface MessageModalDataNonEmptyBase {
  readonly analyticChannel:
    | 'Affordability Calculator'
    | 'Detail'
    | 'List'
    | 'List Item'
    | 'List Item Messaged'
    | 'Permanent Address'
    | 'Programmatic Rent Report'
    | 'Homepage'
  readonly ctaName: string | undefined
  readonly currentTab?: string
  readonly entity: Listable | Property
  readonly isFilterAd: boolean | null | undefined
  readonly isQuestionModal: boolean | undefined
  readonly onlyShowFloorplanGroup: boolean | undefined
  readonly origin: Origin
  readonly selectedFloorplan: FloorplanListing<NormalFloorplanListingData> | undefined
  readonly selectedUnit: Unit | null | undefined
  readonly showAgentInfo: boolean | undefined
  readonly showing: false
  readonly similarListings: readonly never[]
  readonly viewingId: number | undefined
  readonly viewingTime: string | undefined
}

export interface MessageModalDataNonEmptyTour extends MessageModalDataNonEmptyBase {
  readonly equalCTAIntent: 'Tour'
  readonly hasInstantTourAvailability: boolean
}

export interface MessageModalDataNonEmptyNonTour extends MessageModalDataNonEmptyBase {
  readonly equalCTAIntent: 'Apply' | 'Message' | 'Waitlist' | undefined
  readonly hasInstantTourAvailability: boolean | undefined
}

export type MessageModalDataNonEmpty =
  | MessageModalDataNonEmptyNonTour
  | MessageModalDataNonEmptyTour

export type MessageModalData = MessageModalDataEmpty | MessageModalDataNonEmpty

/**
 * The data you need to pass to `showMessagingModal(...)`
 */
export type ShowMessagingModalData = DistributedOmit<
  OptionalIfUndefined<MessageModalDataNonEmpty>,
  'isFilterAd' | 'showing' | 'similarListings'
>

export interface MessageModal {
  readonly data: MessageModalData
  readonly meta: {
    readonly canceled: boolean
    readonly error: unknown
    readonly fetching: boolean
    readonly fetchingKey: number | null
  }
  readonly step: 1 | 2 | null
}

export interface NonEmptyMultiMessageModal {
  readonly analyticChannel:
    | 'Affordability Calculator'
    | 'Detail'
    | 'List'
    | 'List Item'
    | 'List Item Messaged'
    | 'Permanent Address'
    | 'Programmatic Rent Report'
    | 'Homepage'
  readonly dlcMessagedProperties: readonly DlcMessagedProperty[] | undefined
  readonly entity: Listable | Property
  readonly equalCTAIntent?: SendMessageArgs['equalCTAIntent']
  readonly formType?: SendMessageArgs['formType']
  readonly fromNotifyMe?: boolean
  readonly messageData?: SendMessageArgs['messageData']
  readonly origin: Origin
  readonly postLeadData?: PostLeadSignUpData | null | undefined
  readonly selectedFloorplanId?: SendMessageArgs['selectedFloorplanId']
  readonly similarListings: readonly Listable[]
}

export interface EmptyMultiMessageModal {
  readonly analyticChannel: ''
  readonly dlcMessagedProperties: null
  readonly entity: null
  readonly equalCTAIntent?: undefined
  readonly formType?: undefined
  readonly fromNotifyMe: false
  readonly messageData?: null
  readonly origin: ''
  readonly postLeadData: null
  readonly selectedFloorplanId?: undefined
  readonly similarListings: readonly []
}

export type MultiMessageModal = EmptyMultiMessageModal | NonEmptyMultiMessageModal

export interface PostLeadSignUpData {
  readonly email: string | undefined
  readonly name: string | undefined
}

export interface SaveSearchModal {
  readonly entity: Property | null
  readonly filters: Filters
  readonly loading: boolean
  readonly origin:
    | 'MAP'
    | 'am-listing'
    | 'cb-detail'
    | 'ad-cb-detail'
    | 'detail.b'
    | typeof Origin.DETAIL_TOP_SAVE_SEARCH
    | typeof Origin.ADDR_DETAIL_TOP_SAVE_SEARCH
    | typeof Origin.DETAIL_MIDDLE_SAVE_SEARCH
    | 'detail-popup-inactive'
    | 'f-track_prices'
    | `f-track_prices-${string}`
    | `f-popup-${string}`
    | 'nearme'
    | 'nl'
    | 'notify-me-checkbox'
    | ''
    | typeof Origin.GALLERY_FOCUSED
    | typeof Origin.ADDR_GALLERY_FOCUSED
}

export interface SavedSearchOptionModal {
  readonly emailNotificationEnabled: boolean | null
  readonly pushNotificationEnabled: boolean | null
  readonly savedSearch: SavedSearch | null
}

export interface SendMessageArgsSingle extends SendMessageArgsBase {
  readonly dlcMessagedProperties?: readonly DlcMessagedProperty[] | undefined
  readonly isMulti?: false
  multiListables?: never
}
export interface SendMessageArgsMulti extends SendMessageArgsBase {
  readonly dlcMessagedProperties?: undefined
  readonly isMulti: true
  multiListables: readonly Listable[]
}

export type SendMessageArgs = SendMessageArgsSingle | SendMessageArgsMulti

export interface SendMessageArgsBase {
  readonly analyticChannel:
    | 'Affordability Calculator'
    | 'Detail'
    | 'List'
    | 'List Item'
    | 'List Item Messaged'
    | 'Permanent Address'
    | 'Programmatic Rent Report'
    | 'Homepage'
  readonly dlcEnabled?: boolean | undefined
  entity: Listable | Property
  equalCTAIntent?: 'Apply' | 'Message' | 'Tour' | 'Waitlist' | undefined
  readonly floorplan?: FloorplanListing
  readonly formType?: 'MESSAGE_FORM_INLINE' | 'MESSAGE_FORM_MODAL'
  readonly isResidentQuotesChat?: true
  readonly messageData: Mutable<InitialMessageData>
  readonly moveInText?: 'ASAP' | 'In the next 30 days' | 'Later this year' | undefined
  readonly oneTap?: boolean
  readonly selectedFloorplanId?: number | null
  readonly shouldLimitLeads?: boolean
  readonly showMessageSuccessButter?: boolean
  readonly showTourSuccessButterBar?: boolean
}
